import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'withdraw-telegram-stars-to-ton',
  title: 'How to Withdraw Telegram Stars to TON (and Convert Them to Cash)',
  metaTitle: 'How to Withdraw Telegram Stars to TON & Cash Out',
  metaDescription:
    'Step-by-step guide to withdrawing Telegram Stars: Fragment payouts, the 21-day hold, minimum amounts, converting TON to USD or EUR, and how a payment platform simplifies it.',
  keywords: [
    'withdraw telegram stars',
    'telegram stars to ton',
    'convert telegram stars to money',
    'cash out telegram stars',
    'telegram stars withdrawal',
    'fragment stars withdrawal',
  ],
  category: 'Guides',
  datePublished: '2026-09-09',
  dateModified: '2026-10-11',
  readingTimeMin: 7,
  excerpt:
    'You have earned Telegram Stars from your bot, channel or payment links. Now what? This guide explains every step from Stars to TON to dollars or euros, including the 21-day hold, minimum amounts and the cheapest route.',
  keyTakeaways: [
    'Only recipients of Stars (bots, creators, merchants) can withdraw; regular users cannot cash out Stars they bought.',
    'Stars can be withdrawn as TON on Fragment once they are 21 days old; Fragment requires a minimum of 1,000 Stars per withdrawal.',
    'Telegram Stars Payment Platform lets you withdraw from 100 Stars to any TON wallet directly from the dashboard, with the 21-day maturity tracked automatically.',
    'To get cash, send the TON to an exchange (or use a TON wallet with a built-in off-ramp) and sell it for USD, EUR or your local currency.',
  ],
  body: [
    h2('Who can withdraw Telegram Stars?'),
    p(
      'Telegram distinguishes between Stars bought by users and Stars earned by sellers. If you bought Stars to spend them, they are not refundable to cash. If you **received** Stars as a bot owner, channel creator or merchant, you can convert them. This article is for the second group.',
    ),

    h2('Step 1: Wait for the 21-day holding period'),
    p(
      'Every Stars payment is locked for 21 days after it is received. The hold gives Telegram time to process refunds and detect fraud. You can see your total balance immediately, but only the matured part is withdrawable. Payments mature one by one, so a steady stream of sales gives you a steady stream of withdrawable Stars.',
    ),
    callout(
      'In the Telegram Stars Payment Platform dashboard, the **Ledger** page shows the exact date each payment becomes available and your current withdrawable balance, so you never have to compute it yourself.',
    ),

    h2('Step 2: Choose your withdrawal route'),
    p('There are two main routes, depending on how you collect Stars.'),

    h2('Route A: Withdraw directly on Fragment (own bot or channel)'),
    ol([
      'Open the bot or channel settings in Telegram and go to the **Monetization / Stars balance** section.',
      'Tap **Withdraw via Fragment**. You will be redirected to [fragment.com](https://fragment.com) and asked to confirm with your Telegram account.',
      'Connect a TON wallet (Tonkeeper, MyTonWallet, Telegram Wallet, etc.).',
      'Choose the amount of matured Stars to convert. Fragment shows the TON you will receive at the current rate. The **minimum is 1,000 Stars**.',
      'Confirm. The TON arrives in your wallet, usually within minutes.',
    ]),
    p(
      'Alternatively, the same balance screen lets you spend Stars on **Telegram Ads** to promote your bot or channel, often at a discounted rate compared with paying for ads in TON. If you plan to reinvest in growth, this can be worth more than cashing out.',
    ),

    h2('Route B: Withdraw from Telegram Stars Payment Platform'),
    p(
      'If you accept Stars through payment links on the platform, you never deal with Fragment yourself. The platform aggregates all your payments and pays you in TON:',
    ),
    ol([
      'Go to **Settings** and save your TON wallet address (any standard TON address works).',
      'Open **Withdraws → New**. The form shows your available (matured) balance.',
      'Enter the amount. The **minimum is 100 Stars**, ten times lower than Fragment, which makes it accessible for small creators.',
      'Submit. The request appears with status *pending*. A 3% platform fee is applied.',
      'The team reviews and executes the payout to your TON address. You get an on-chain reference in the ledger once it is paid.',
    ]),
    p(
      'Payouts are reviewed manually as an anti-fraud measure, which is also why the platform can offer a much lower minimum. Every state change (pending, approved, paid, rejected) is visible in the dashboard.',
    ),

    h2('Step 3: Convert TON to USD, EUR or local currency'),
    p('Once the TON is in your wallet, cashing out is the same as with any cryptocurrency:'),
    ul([
      '**Centralised exchange**: send TON to Binance, Bybit, OKX, Kraken or another exchange that lists TON, sell it for USDT or fiat and withdraw to your bank account.',
      '**Wallet off-ramp**: Telegram\'s built-in Wallet and some third-party wallets offer peer-to-peer or card off-ramps directly in the app.',
      '**Keep it as TON or USDT**: if you want to avoid banking friction, swap TON to USDT on TON and pay suppliers in stablecoins.',
    ]),
    p('Always double-check withdrawal addresses and network (TON mainnet) before sending; crypto transfers cannot be reversed.'),

    h2('Comparison of the two routes'),
    table(
      ['', 'Fragment (direct)', 'Telegram Stars Payment Platform'],
      [
        ['Minimum withdrawal', '1,000 Stars', '100 Stars'],
        ['Holding period', '21 days', '21 days (tracked automatically)'],
        ['Fee', 'Conversion rate only', '3% platform fee'],
        ['Requires your own bot', 'Yes', 'No'],
        ['Ledger and payout history', 'Basic', 'Full ledger, every payment and payout'],
        ['Processing', 'Instant on-chain', 'Reviewed, then paid on-chain'],
      ],
    ),

    h2('Taxes and bookkeeping'),
    p(
      'Stars income is income. Most jurisdictions tax it at the time you receive the Stars or at the time you convert them to fiat, depending on local rules. Export your ledger regularly and keep the on-chain transaction references of each payout. A clean ledger is one of the main reasons sellers prefer a platform over manual tracking.',
    ),

    h2('Common problems and fixes'),
    ul([
      '**"Withdraw" button is greyed out**: your Stars are not 21 days old yet, or you are below the minimum amount.',
      '**Wrong TON address**: funds sent to a wrong address are lost. Copy-paste from your wallet and verify the first and last characters.',
      '**Rate looks lower than expected**: the Stars-to-TON rate on Fragment follows the TON price. Consider withdrawing in several batches to average the rate.',
      '**Pending for a long time** on the platform: payouts are reviewed by a human; contact support with your request ID if it exceeds the announced timeframe.',
    ]),
  ],
  faq: [
    {
      q: 'Can I withdraw Telegram Stars to PayPal or a bank account?',
      a: 'Not directly. Telegram pays out Stars in TON. You then sell the TON on an exchange and withdraw fiat to your bank account.',
    },
    {
      q: 'What is the minimum amount of Stars I can withdraw?',
      a: '1,000 Stars on Fragment. On Telegram Stars Payment Platform the minimum is 100 Stars.',
    },
    {
      q: 'Why do I have to wait 21 days to withdraw Stars?',
      a: 'Telegram holds Stars for 21 days to cover refunds and fraud checks. The rule applies to every seller, whether you use Fragment or a payment platform.',
    },
    {
      q: 'How long does a TON withdrawal take?',
      a: 'On Fragment the transfer is on-chain and usually completes within minutes. On the platform, requests are reviewed first and then paid on-chain; you can follow the status in your dashboard.',
    },
    {
      q: 'Is it better to spend Stars on Telegram Ads or withdraw them?',
      a: 'If you are growing a bot or channel, Stars spent on Telegram Ads can be worth more than their TON value because of the discounted ad rate. If you need cash, withdraw to TON.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-refunds-21-day-hold',
    'telegram-stars-fees-explained',
    'telegram-stars-price-usd-ton',
  ],
};

export default article;
